import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDoorOpen, faClipboardList, faArrowUp, faSearch, faBed, faUser,
  faPhone, faCalendarDay, faMoon, faMoneyBill, faPrint, faCheck,
  faXmark, faPlus, faArrowRight, faPersonShelter, faBaby, faChild,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./FrontDeskPage.css";
import { BOOKING_SOURCES, emptyBookingForm } from "../Bookings/BookingsPage";
import { breakdownFromSubtotal } from "../../utils/pricing";
import { usePricingConfig } from "../../utils/usePricingConfig";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

function localISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

// Only allows + at start, digits only after — no auto country code
function formatPhone(raw: string): string {
  return raw.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");
}

// Strips country code prefix for fuzzy phone matching
function normalizePhone(p: string): string {
  const digits = p.replace(/\D/g, "");
  if (digits.startsWith("20") && digits.length > 10) return digits.slice(2);
  if (digits.startsWith("0")) return digits.slice(1);
  return digits;
}
function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
function generateBookingId(roomId: string) {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const rand = Math.floor(Math.random() * 900) + 100;
  return `${roomId || "WI"}-${date}-${rand}`;
}


interface RoomGuest {
  id: string;
  name: string;
  phone: string;
}

interface RoomEntry {
  room: any;
  rate: number;
  total: number;
  autoCalc: boolean;
  loading: boolean;
  adults_count: number;
  children_count: number;
  extra_bed: boolean;
  extra_bed_price: number;
  cot: boolean;
  cot_price: number;
  leaderName: string;
  leaderPhone: string;
  extraGuests: RoomGuest[];
}

// ── Walk-in Quick Booking ─────────────────────────────────────
function WalkInTab({ authHeaders }: { authHeaders: Record<string,string> }) {
  const today = localISO(new Date());
  const guestNameRef = useRef<HTMLInputElement>(null);
  const [rooms, setRooms]         = useState<any[]>([]);
  const [guests, setGuests]       = useState<any[]>([]);
  const [bookings, setBookings]   = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [success, setSuccess]     = useState("");
  const [error, setError]         = useState("");
  const [roomSearch, setRoomSearch] = useState("");

  // Multi-room cart
  const [selectedRooms, setSelectedRooms] = useState<RoomEntry[]>([]);

  // Form state
  const [guestName, setGuestName]         = useState("");
  const [phone, setPhone]                 = useState("");
  const [checkIn, setCheckIn]             = useState(today);
  const [checkOut, setCheckOut]           = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); return localISO(d);
  });
  const [source, setSource]               = useState("direct");
  const [notes, setNotes]                 = useState("");
  const [guestSearch, setGuestSearch]     = useState("");
  const [guestDropOpen, setGuestDropOpen] = useState(false);
  const [leaderDropRoom, setLeaderDropRoom] = useState<string | null>(null);
  const [extraGuestDropKey, setExtraGuestDropKey] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [ratesMap, setRatesMap] = useState<Record<string, number>>({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/api/rooms`, { headers: authHeaders }),
      axios.get(`${API_BASE}/api/dashboard/guests`, { headers: authHeaders }),
      axios.get(`${API_BASE}/api/dashboard/bookings`, { headers: authHeaders }),
      axios.get(`${API_BASE}/api/roomrates`, { headers: authHeaders }),
    ]).then(([r, g, b, rates]) => {
      setRooms(r.data || []);
      setGuests(Array.isArray(g.data) ? g.data : []);
      const bkgs = Array.isArray(b.data) ? b.data : b.data?.bookings ?? [];
      setBookings(bkgs);
      const map: Record<string, number> = {};
      (rates.data || []).forEach((rt: any) => { map[String(rt.roomid)] = Number(rt.baserate) || 0; });
      setRatesMap(map);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const nights = useMemo(() => {
    const d = (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86400000;
    return d > 0 ? Math.round(d) : 1;
  }, [checkIn, checkOut]);

  // When dates change: clear cart (rooms may no longer be available) and recalc
  useEffect(() => {
    setSelectedRooms([]);
  }, [checkIn, checkOut]);

  const grandTotal = selectedRooms.reduce((s, e) => s + e.total, 0); // pre-tax subtotal
  const bk = breakdownFromSubtotal(grandTotal, nights);              // tax / service / total / deposit (1 night)

  // Available rooms: not OOS + no conflicting active booking
  const availableRooms = useMemo(() => {
    return rooms.filter(r => {
      if ((r.status || "").toLowerCase() === "out-of-service") return false;
      const hasConflict = bookings.some(b => {
        const roomIds: string[] = b.allRoomIds?.length ? b.allRoomIds : [b.roomId];
        if (!roomIds.includes(r.roomid)) return false;
        if (["cancelled","deleted"].includes((b.status || "").toLowerCase())) return false;
        if (!b.checkIn || !b.checkOut) return false;
        const bci = String(b.checkIn).slice(0, 10);
        const bco = String(b.checkOut).slice(0, 10);
        return bci < checkOut && bco > checkIn;
      });
      return !hasConflict;
    });
  }, [rooms, bookings, checkIn, checkOut]);

  const filteredRooms = roomSearch
    ? availableRooms.filter(r =>
        String(r.roomnumber).includes(roomSearch) ||
        (r.roomtype || "").toLowerCase().includes(roomSearch.toLowerCase())
      )
    : availableRooms;

  const filteredGuests = guestSearch
    ? guests.filter(g =>
        (g.name || "").toLowerCase().includes(guestSearch.toLowerCase()) ||
        String(g.phoneNumber || "").includes(guestSearch)
      )
    : guests;

  const isSelected = (roomid: string) => selectedRooms.some(e => e.room.roomid === roomid);

  const toggleRoom = async (r: any) => {
    if (isSelected(r.roomid)) {
      setSelectedRooms(prev => prev.filter(e => e.room.roomid !== r.roomid));
      return;
    }
    // Apply cached base rate immediately — no loading flicker
    const baseRate = ratesMap[String(r.roomid)] ?? 0;
    setSelectedRooms(prev => [
      ...prev,
      { room: r, rate: baseRate, total: baseRate * nights, autoCalc: baseRate > 0, loading: baseRate === 0,
        adults_count: 1, children_count: 0, extra_bed: false, extra_bed_price: 0, cot: false, cot_price: 0,
        leaderName: "", leaderPhone: "", extraGuests: [] },
    ]);
    // Refine with calc endpoint (accounts for date-specific rate overrides)
    try {
      const calcRes = await axios.get(`${API_BASE}/api/roomrates/calc`, {
        params: { roomId: r.roomid, checkin: checkIn, checkout: checkOut },
        headers: authHeaders,
      });
      const calcTotal = Number(calcRes.data?.total) || 0;
      const calcNights = (calcRes.data?.nights || []).length || nights;
      const calcRate = calcNights > 0 ? Math.round(calcTotal / calcNights) : baseRate;
      if (calcTotal > 0) {
        setSelectedRooms(prev => prev.map(e =>
          e.room.roomid === r.roomid
            ? { ...e, rate: calcRate, total: calcRoomTotal(e, { rate: calcRate }), autoCalc: true, loading: false }
            : e
        ));
      } else if (baseRate === 0) {
        setSelectedRooms(prev => prev.map(e =>
          e.room.roomid === r.roomid ? { ...e, loading: false } : e
        ));
      }
    } catch {
      setSelectedRooms(prev => prev.map(e =>
        e.room.roomid === r.roomid ? { ...e, loading: false } : e
      ));
    }
  };

  const calcRoomTotal = (e: RoomEntry, overrides: Partial<RoomEntry> = {}) => {
    const merged = { ...e, ...overrides };
    const base = merged.rate * nights;
    const extras = (merged.extra_bed ? Number(merged.extra_bed_price) : 0)
                 + (merged.cot       ? Number(merged.cot_price)       : 0);
    return base + extras;
  };

  const updateRoomRate = (roomid: string, rate: number) => {
    setSelectedRooms(prev => prev.map(e =>
      e.room.roomid === roomid
        ? { ...e, rate, total: calcRoomTotal(e, { rate }), autoCalc: false }
        : e
    ));
  };

  const updateRoomTotal = (roomid: string, total: number) => {
    setSelectedRooms(prev => prev.map(e =>
      e.room.roomid === roomid ? { ...e, total, autoCalc: false } : e
    ));
  };

  const updateRoomExtra = (roomid: string, field: keyof RoomEntry, value: any) => {
    setSelectedRooms(prev => prev.map(e => {
      if (e.room.roomid !== roomid) return e;
      const updated = { ...e, [field]: value };
      return { ...updated, total: calcRoomTotal(e, { [field]: value }) };
    }));
  };

  const updateLeader = (roomid: string, field: "leaderName" | "leaderPhone", value: string) => {
    setSelectedRooms(prev => prev.map(e =>
      e.room.roomid === roomid ? { ...e, [field]: value } : e
    ));
  };

  const addExtraGuest = (roomid: string) => {
    setSelectedRooms(prev => prev.map(e =>
      e.room.roomid === roomid
        ? { ...e, adults_count: e.adults_count + 1, extraGuests: [...e.extraGuests, { id: String(Date.now()), name: "", phone: "" }] }
        : e
    ));
  };

  const updateExtraGuest = (roomid: string, guestId: string, field: "name" | "phone", value: string) => {
    setSelectedRooms(prev => prev.map(e =>
      e.room.roomid === roomid
        ? { ...e, extraGuests: e.extraGuests.map(g => g.id === guestId ? { ...g, [field]: value } : g) }
        : e
    ));
  };

  const removeExtraGuest = (roomid: string, guestId: string) => {
    setSelectedRooms(prev => prev.map(e =>
      e.room.roomid === roomid
        ? { ...e, adults_count: Math.max(1, e.adults_count - 1), extraGuests: e.extraGuests.filter(g => g.id !== guestId) }
        : e
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (selectedRooms.length === 0) {
      setError("Select at least one room.");
      return;
    }
    if (!guestName.trim()) {
      setError("Guest name is required.");
      guestNameRef.current?.focus();
      guestNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // Validate leader for every room
    for (const e of selectedRooms) {
      if (!e.leaderName.trim()) {
        setError(`Room ${e.room.roomnumber}: enter a room leader name.`);
        return;
      }
      if (!e.leaderPhone.trim()) {
        setError(`Room ${e.room.roomnumber}: room leader phone is required.`);
        return;
      }
    }
    setSaving(true); setError("");
    try {
      const firstRoom = selectedRooms[0].room;
      const bookingId = generateBookingId(firstRoom.roomid);
      const cleanPhone = phone.replace(/\D/g, "");
      const roomids = selectedRooms.map(e => e.room.roomid);
      const roomTotals: Record<string, number> = {};
      const roomExtras: Record<string, object> = {};
      selectedRooms.forEach((e, idx) => {
        roomTotals[e.room.roomid] = e.total;
        const effectiveLeaderName  = e.leaderName.trim();
        const effectiveLeaderPhone = e.leaderPhone.replace(/\D/g, "");

        // Build ordered guest list: leader first, then extras (skip empty names)
        const roomGuestsList = [
          { name: effectiveLeaderName, phone: effectiveLeaderPhone },
          ...e.extraGuests
            .filter(g => g.name.trim())
            .map(g => ({ name: g.name.trim(), phone: g.phone.replace(/\D/g, "") })),
        ];

        roomExtras[e.room.roomid] = {
          extra_bed:        e.extra_bed,
          extra_bed_price:  e.extra_bed_price,
          cot:              e.cot,
          cot_price:        e.cot_price,
          children_count:   e.children_count,
          adults_count:     e.adults_count,
          room_guest_name:  effectiveLeaderName,
          room_guest_phone: effectiveLeaderPhone,
          room_guests:      roomGuestsList,
        };
      });

      await axios.post(`${API_BASE}/api/bookings`, {
        bookingid: bookingId,
        roomid: firstRoom.roomid,
        roomids,
        roomTotals,
        roomExtras,
        guestname: guestName,
        phonenumber: cleanPhone ? Number(cleanPhone) : null,
        checkin: checkIn, checkout: checkOut, nights, total: bk.total,
        status: "confirmed",
        guest_count: selectedRooms.reduce((s, e) => s + e.adults_count + e.children_count, 0),
        source, notes: notes || null, createdat: new Date().toISOString(),
      }, { headers: authHeaders });

      const roomLabels = selectedRooms.map(e => `Room ${e.room.roomnumber}`).join(", ");
      setSuccess(`Booking ${bookingId} confirmed for ${guestName} — ${roomLabels}`);
      setSelectedRooms([]); setGuestName(""); setPhone(""); setNotes(""); setSource("direct"); setGuestSearch(""); setSubmitAttempted(false);
      // Refresh rooms + bookings so newly booked rooms disappear from available list
      const [freshRooms, freshBookings] = await Promise.all([
        axios.get(`${API_BASE}/api/rooms`, { headers: authHeaders }),
        axios.get(`${API_BASE}/api/dashboard/bookings`, { headers: authHeaders }),
      ]);
      setRooms(freshRooms.data || []);
      setBookings(Array.isArray(freshBookings.data) ? freshBookings.data : freshBookings.data?.bookings ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? "Booking failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="fd-loading">Loading rooms…</div>;

  return (
    <div className="fd-walkin">
      {success && (
        <div className="fd-success">
          <FontAwesomeIcon icon={faCheck} /> {success}
          <button onClick={() => setSuccess("")}>×</button>
        </div>
      )}
      {error && <div className="fd-error"><FontAwesomeIcon icon={faXmark} /> {error}</div>}

      {/* ── Date bar ─── */}
      <div className="fd-date-bar">
        <div className="fd-date-bar-section">
          <span className="fd-date-bar-lbl">Check-in</span>
          <input
            className="fd-date-bar-input" type="date"
            value={checkIn} min={today}
            onChange={e => setCheckIn(e.target.value)}
          />
        </div>
        <FontAwesomeIcon icon={faArrowRight} className="fd-date-bar-arrow" />
        <div className="fd-date-bar-section">
          <span className="fd-date-bar-lbl">Check-out</span>
          <input
            className="fd-date-bar-input" type="date"
            value={checkOut} min={checkIn}
            onChange={e => setCheckOut(e.target.value)}
          />
        </div>
        <div className="fd-date-bar-nights">
          <FontAwesomeIcon icon={faMoon} />
          <span>{nights} night{nights !== 1 ? "s" : ""}</span>
        </div>
        <div className="fd-date-bar-hint">
          <FontAwesomeIcon icon={faBed} /> Click rooms to add them — multi-room booking supported
        </div>
      </div>

      <div className="fd-walkin-layout">
        {/* Room picker */}
        <div className="fd-panel">
          <div className="fd-panel-head">
            <div className="fd-panel-icon fd-panel-icon-green"><FontAwesomeIcon icon={faBed} /></div>
            <div style={{ flex: 1 }}>
              <h3 className="fd-panel-title">Available Rooms</h3>
              <p className="fd-panel-dates">{fmt(checkIn)} → {fmt(checkOut)}</p>
            </div>
            <span className="fd-panel-count" style={{ background: "rgba(16,185,129,0.1)", color: "#059669" }}>{availableRooms.length}</span>
          </div>
          <div className="fd-search-wrap">
            <FontAwesomeIcon icon={faSearch} className="fd-search-icon" />
            <input
              className="fd-search-input"
              placeholder="Search rooms…" value={roomSearch}
              onChange={e => setRoomSearch(e.target.value)}
            />
          </div>
          <div className="fd-room-list">
            {filteredRooms.map(r => {
              const sel = isSelected(r.roomid);
              return (
                <div
                  key={r.roomid}
                  className={`fd-room-item ${sel ? "selected" : ""}`}
                  onClick={() => toggleRoom(r)}
                >
                  <div className="fd-room-info">
                    {r.roomtype && <span className="fd-room-type">{r.roomtype}</span>}
                    <span className="fd-room-number">Room {r.roomnumber}</span>
                    <span className="fd-room-id">{r.roomid}</span>
                  </div>
                  {sel && <FontAwesomeIcon icon={faCheck} className="fd-room-check" />}
                </div>
              );
            })}
            {filteredRooms.length === 0 && (
              <div className="fd-empty">No available rooms for these dates</div>
            )}
          </div>
        </div>

        {/* Booking form */}
        <div className="fd-panel">
          <div className="fd-panel-head">
            <div className="fd-panel-icon fd-panel-icon-navy"><FontAwesomeIcon icon={faUser} /></div>
            <h3 className="fd-panel-title">Guest &amp; Booking Details</h3>
            {selectedRooms.length > 0 && (
              <span className="fd-panel-count" style={{ background: "rgba(201,168,76,0.1)", color: "#92400e" }}>
                {selectedRooms.length} room{selectedRooms.length > 1 ? "s" : ""}
              </span>
            )}
          </div>

          <div className="fd-form">
            {/* Selected rooms cart */}
            {selectedRooms.length === 0 ? (
              <div className="fd-no-room">← Click rooms on the left to add them</div>
            ) : (
              <div className="fd-rooms-cart">
                {selectedRooms.map((entry, roomIdx) => (
                  <div key={entry.room.roomid} className="fd-cart-item">
                    <div className="fd-cart-item-main">
                      <div className="fd-cart-item-left">
                        <FontAwesomeIcon icon={faBed} className="fd-cart-item-icon" />
                        <div>
                          <div className="fd-cart-item-name-row">
                            <span className="fd-cart-item-num">Room {entry.room.roomnumber}</span>
                            <span className="fd-cart-item-id">{entry.room.roomid}</span>
                          </div>
                          <span className="fd-cart-item-type">{entry.room.roomtype || "—"}</span>
                        </div>
                      </div>
                      <div className="fd-cart-item-right">
                        <div className="fd-cart-rate-wrap">
                          <span className="fd-cart-rate-lbl">EGP/night</span>
                          <input
                            className="fd-cart-rate-input"
                            type="number" min={0}
                            value={entry.loading ? "" : entry.rate}
                            placeholder={entry.loading ? "…" : "0"}
                            disabled={entry.loading}
                            onChange={e => updateRoomRate(entry.room.roomid, parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="fd-cart-total-wrap">
                          <span className="fd-cart-rate-lbl">Total</span>
                          <input
                            className="fd-cart-total-input"
                            type="number" min={0}
                            value={entry.loading ? "" : entry.total}
                            placeholder={entry.loading ? "…" : "0"}
                            disabled={entry.loading}
                            onChange={e => updateRoomTotal(entry.room.roomid, parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        {entry.autoCalc && <span className="fd-cart-auto">Auto</span>}
                        <button
                          className="fd-cart-remove"
                          type="button"
                          onClick={() => setSelectedRooms(prev => prev.filter(e => e.room.roomid !== entry.room.roomid))}
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </div>
                    </div>

                    {/* Extras row — inside the card */}
                    <div className="fd-cart-extras">
                      <div className="fd-cart-extra-field">
                        <span className="fd-cart-extra-lbl"><FontAwesomeIcon icon={faUser} /> Adults</span>
                        <div className="fd-cart-stepper">
                          <button type="button" onClick={() => {
                            if (entry.extraGuests.length > 0) removeExtraGuest(entry.room.roomid, entry.extraGuests[entry.extraGuests.length - 1].id);
                          }} disabled={entry.adults_count <= 1}>−</button>
                          <span>{entry.adults_count}</span>
                          <button type="button" onClick={() => addExtraGuest(entry.room.roomid)}>+</button>
                        </div>
                      </div>

                      <div className="fd-cart-extra-field">
                        <span className="fd-cart-extra-lbl"><FontAwesomeIcon icon={faChild} /> Children</span>
                        <div className="fd-cart-stepper">
                          <button type="button" onClick={() => updateRoomExtra(entry.room.roomid, "children_count", Math.max(0, entry.children_count - 1))}>−</button>
                          <span>{entry.children_count}</span>
                          <button type="button" onClick={() => updateRoomExtra(entry.room.roomid, "children_count", entry.children_count + 1)}>+</button>
                        </div>
                      </div>

                      <div className="fd-cart-extra-field">
                        <label className="fd-cart-toggle">
                          <input type="checkbox" checked={entry.extra_bed} onChange={e => updateRoomExtra(entry.room.roomid, "extra_bed", e.target.checked)} />
                          <span className="fd-cart-toggle-track" />
                          <span className="fd-cart-extra-lbl"><FontAwesomeIcon icon={faPersonShelter} /> Extra Bed</span>
                        </label>
                        {entry.extra_bed && (
                          <input
                            className="fd-cart-extra-price"
                            type="number" min={0} placeholder="Price"
                            value={entry.extra_bed_price || ""}
                            onChange={e => updateRoomExtra(entry.room.roomid, "extra_bed_price", parseFloat(e.target.value) || 0)}
                          />
                        )}
                      </div>

                      <div className="fd-cart-extra-field">
                        <label className="fd-cart-toggle">
                          <input type="checkbox" checked={entry.cot} onChange={e => updateRoomExtra(entry.room.roomid, "cot", e.target.checked)} />
                          <span className="fd-cart-toggle-track" />
                          <span className="fd-cart-extra-lbl"><FontAwesomeIcon icon={faBaby} /> Baby Cot</span>
                        </label>
                        {entry.cot && (
                          <input
                            className="fd-cart-extra-price"
                            type="number" min={0} placeholder="Price"
                            value={entry.cot_price || ""}
                            onChange={e => updateRoomExtra(entry.room.roomid, "cot_price", parseFloat(e.target.value) || 0)}
                          />
                        )}
                      </div>

                    </div>

                    {/* Room leader — required for every room */}
                    <div className="fd-cart-leader-section">
                      <span className="fd-cart-leader-title">
                        <FontAwesomeIcon icon={faUser} /> Room Leader *
                      </span>
                      <div className="fd-cart-leader-fields">
                        {/* Name — left */}
                        <input
                          className={`fd-cart-leader-input${submitAttempted && !entry.leaderName.trim() ? " fd-input--invalid" : ""}`}
                          type="text"
                          placeholder="Full name *"
                          value={entry.leaderName}
                          onChange={e => updateLeader(entry.room.roomid, "leaderName", e.target.value)}
                        />
                        {/* Phone — right, searches existing guests by number */}
                        <div style={{ position: "relative" }}>
                          <input
                            className={`fd-cart-leader-input${submitAttempted && !entry.leaderPhone.trim() ? " fd-input--invalid" : ""}`}
                            type="tel"
                            placeholder="+20 1XX XXX XXXX *"
                            value={entry.leaderPhone}
                            onChange={e => {
                              const formatted = formatPhone(e.target.value);
                              updateLeader(entry.room.roomid, "leaderPhone", formatted);
                              setLeaderDropRoom(entry.room.roomid);
                            }}
                            onFocus={() => setLeaderDropRoom(entry.room.roomid)}
                            onBlur={() => setTimeout(() => setLeaderDropRoom(null), 150)}
                          />
                          {leaderDropRoom === entry.room.roomid && normalizePhone(entry.leaderPhone).length >= 3 && (() => {
                            const q = normalizePhone(entry.leaderPhone);
                            const matches = (guests as any[]).filter((g: any) =>
                              normalizePhone(String(g.phoneNumber || "")).includes(q)
                            ).slice(0, 8);
                            return matches.length > 0 ? (
                              <div className="fd-guest-dropdown">
                                {matches.map((g: any) => (
                                  <div
                                    key={g.id}
                                    className="fd-guest-option"
                                    onMouseDown={() => {
                                      updateLeader(entry.room.roomid, "leaderPhone", String(g.phoneNumber || ""));
                                      updateLeader(entry.room.roomid, "leaderName", g.name);
                                      setLeaderDropRoom(null);
                                    }}
                                  >
                                    <span className="fd-guest-option-name">{g.name}</span>
                                    <span className="fd-guest-option-phone">{g.phoneNumber}</span>
                                  </div>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Extra guests section */}
                    <div className="fd-cart-guests-section">
                      {entry.extraGuests.length > 0 && (
                        <div className="fd-cart-guests-list">
                          {entry.extraGuests.map(g => (
                            <div key={g.id} className="fd-cart-guest-row">
                              <input
                                className="fd-cart-guest-input"
                                type="text"
                                placeholder="Guest name"
                                value={g.name}
                                onChange={e => updateExtraGuest(entry.room.roomid, g.id, "name", e.target.value)}
                              />
                              <div style={{ position: "relative" }}>
                                <input
                                  className="fd-cart-guest-input"
                                  type="tel"
                                  placeholder="+20 1XX XXX XXXX"
                                  value={g.phone}
                                  onChange={e => {
                                    updateExtraGuest(entry.room.roomid, g.id, "phone", formatPhone(e.target.value));
                                    setExtraGuestDropKey(`${entry.room.roomid}-${g.id}`);
                                  }}
                                  onFocus={() => setExtraGuestDropKey(`${entry.room.roomid}-${g.id}`)}
                                  onBlur={() => setTimeout(() => setExtraGuestDropKey(null), 150)}
                                />
                                {extraGuestDropKey === `${entry.room.roomid}-${g.id}` && normalizePhone(g.phone).length >= 3 && (() => {
                                  const q = normalizePhone(g.phone);
                                  const matches = (guests as any[]).filter((gg: any) =>
                                    normalizePhone(String(gg.phoneNumber || "")).includes(q)
                                  ).slice(0, 8);
                                  return matches.length > 0 ? (
                                    <div className="fd-guest-dropdown">
                                      {matches.map((gg: any) => (
                                        <div
                                          key={gg.id}
                                          className="fd-guest-option"
                                          onMouseDown={() => {
                                            updateExtraGuest(entry.room.roomid, g.id, "phone", String(gg.phoneNumber || ""));
                                            updateExtraGuest(entry.room.roomid, g.id, "name", gg.name);
                                            setExtraGuestDropKey(null);
                                          }}
                                        >
                                          <span className="fd-guest-option-name">{gg.name}</span>
                                          <span className="fd-guest-option-phone">{gg.phoneNumber}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                              <button
                                type="button"
                                className="fd-cart-guest-remove"
                                onClick={() => removeExtraGuest(entry.room.roomid, g.id)}
                              >
                                <FontAwesomeIcon icon={faXmark} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        type="button"
                        className="fd-cart-add-guest"
                        onClick={() => addExtraGuest(entry.room.roomid)}
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add Guest
                      </button>
                    </div>
                  </div>
                ))}
                <div className="fd-cart-breakdown">
                  <div className="fd-bk-row"><span>Subtotal</span><span>EGP {bk.subtotal.toLocaleString()}</span></div>
                  {bk.tax > 0 && <div className="fd-bk-row"><span>Tax ({(bk.taxRate * 100).toFixed(0)}%)</span><span>EGP {bk.tax.toLocaleString()}</span></div>}
                  {bk.service > 0 && <div className="fd-bk-row"><span>Service ({(bk.serviceRate * 100).toFixed(0)}%)</span><span>EGP {bk.service.toLocaleString()}</span></div>}
                  <div className="fd-cart-grand-total">
                    <span>Total</span>
                    <span>EGP {bk.total.toLocaleString()}</span>
                  </div>
                  <div className="fd-bk-deposit">
                    <span>Deposit (1 night)</span>
                    <span>EGP {bk.deposit.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Guest search */}
              <div className="fd-field" style={{ position: "relative" }}>
                <label className="fd-label">Guest Name *</label>
                <input
                  ref={guestNameRef}
                  className={`fd-input${submitAttempted && !guestName.trim() ? " fd-input--invalid" : ""}`}
                  type="text" value={guestName} required placeholder="Search or type name…"
                  onChange={e => { setGuestName(e.target.value); setGuestSearch(e.target.value); setGuestDropOpen(true); }}
                  onFocus={() => setGuestDropOpen(true)}
                  onBlur={() => setTimeout(() => setGuestDropOpen(false), 150)}
                />
                {guestDropOpen && filteredGuests.length > 0 && (
                  <div className="fd-guest-dropdown">
                    {filteredGuests.slice(0, 20).map((g: any) => (
                      <div
                        key={g.id}
                        className="fd-guest-option"
                        onMouseDown={() => { setGuestName(g.name); setPhone(String(g.phoneNumber || "")); setGuestDropOpen(false); }}
                      >
                        <span className="fd-guest-option-name">{g.name}</span>
                        <span className="fd-guest-option-phone">{g.phoneNumber}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="fd-two-col">
                <div className="fd-field">
                  <label className="fd-label">Phone</label>
                  <input className="fd-input" type="tel" value={phone} onChange={e => setPhone(formatPhone(e.target.value))} placeholder="+20 1XX XXX XXXX" />
                </div>
                <div className="fd-field">
                  <label className="fd-label">Source</label>
                  <select className="fd-select" value={source} onChange={e => setSource(e.target.value)}>
                    {BOOKING_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="fd-field">
                <label className="fd-label">Notes</label>
                <textarea className="fd-textarea" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Special requests…" rows={2} />
              </div>

              {selectedRooms.length > 0 && (
                <div className="fd-summary">
                  <span><FontAwesomeIcon icon={faMoon} /> {nights} night{nights !== 1 ? "s" : ""}</span>
                  <span><FontAwesomeIcon icon={faBed} /> {selectedRooms.length} room{selectedRooms.length > 1 ? "s" : ""}</span>
                  {grandTotal > 0 && <span><FontAwesomeIcon icon={faMoneyBill} /> EGP {grandTotal.toLocaleString()}</span>}
                </div>
              )}

              <button type="submit" className="fd-submit-btn" disabled={saving || selectedRooms.length === 0}>
                <FontAwesomeIcon icon={faPlus} />
                {saving ? "Confirming…" : `Confirm Walk-in Booking${selectedRooms.length > 1 ? ` (${selectedRooms.length} rooms)` : ""}`}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Arrivals Briefing ────────────────────────────────────────
function ArrivalsBriefingTab({ authHeaders }: { authHeaders: Record<string,string> }) {
  const today = localISO(new Date());
  const [arrivals, setArrivals]     = useState<any[]>([]);
  const [departures, setDepartures] = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [view, setView]             = useState<"arrivals" | "departures">("arrivals");

  useEffect(() => {
    axios.get(`${API_BASE}/api/dashboard/bookings`, { headers: authHeaders })
      .then(res => {
        const bkgs = Array.isArray(res.data) ? res.data : res.data?.bookings ?? [];
        setArrivals(bkgs.filter((b: any) => {
          const ci = String(b.checkIn || "").slice(0, 10);
          const st = (b.effectiveStatus || b.status || "").toLowerCase();
          return ci === today && ["confirmed","checked-in"].includes(st);
        }));
        setDepartures(bkgs.filter((b: any) => {
          const co = String(b.checkOut || "").slice(0, 10);
          const st = (b.effectiveStatus || b.status || "").toLowerCase();
          return co === today && st === "checked-in";
        }));
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handlePrint = () => window.print();

  const list = view === "arrivals" ? arrivals : departures;

  if (loading) return <div className="fd-loading">Loading…</div>;

  return (
    <div className="fd-briefing">
      <div className="fd-briefing-header no-print">
        <div className="fd-brief-tabs">
          <button className={`fd-brief-tab ${view === "arrivals" ? "active" : ""}`} onClick={() => setView("arrivals")}>
            <FontAwesomeIcon icon={faArrowRight} /> Arrivals ({arrivals.length})
          </button>
          <button className={`fd-brief-tab ${view === "departures" ? "active" : ""}`} onClick={() => setView("departures")}>
            <FontAwesomeIcon icon={faArrowRight} style={{ transform: "rotate(180deg)" }} /> Departures ({departures.length})
          </button>
        </div>
        <button className="fd-print-btn" onClick={handlePrint}>
          <FontAwesomeIcon icon={faPrint} /> Print Briefing
        </button>
      </div>

      {/* Printable header */}
      <div className="fd-print-header print-only">
        <h2>{view === "arrivals" ? "Arrivals" : "Departures"} Briefing — {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h2>
        <p>{list.length} guest{list.length !== 1 ? "s" : ""}</p>
      </div>

      {list.length === 0 ? (
        <div className="fd-empty">No {view} today.</div>
      ) : (
        <div className="fd-briefing-table">
          <div className="fd-brief-thead">
            <span>Guest</span>
            <span>Room</span>
            <span>Check-in</span>
            <span>Check-out</span>
            <span>Nights</span>
            <span>Total</span>
            <span>Notes</span>
          </div>
          {list.map((b: any) => (
            <div key={b.bookingId} className="fd-brief-row">
              <div>
                <div className="fd-brief-name">{b.guestName || "—"}</div>
                <div className="fd-brief-phone">{b.phone || ""}</div>
              </div>
              <div className="fd-brief-room">Room {b.roomNumber || "—"}</div>
              <div className="fd-brief-val">{fmt(b.checkIn)}</div>
              <div className="fd-brief-val">{fmt(b.checkOut)}</div>
              <div className="fd-brief-val">{b.nights ?? "—"}</div>
              <div className="fd-brief-val">EGP {Number(b.total || 0).toLocaleString()}</div>
              <div className="fd-brief-notes">{(b as any).notes || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Room Upgrade ─────────────────────────────────────────────
function UpgradeTab({ authHeaders }: { authHeaders: Record<string,string> }) {
  const [inHouse, setInHouse]       = useState<any[]>([]);
  const [rooms, setRooms]           = useState<any[]>([]);
  const [bookings, setBookings]     = useState<any[]>([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState<any>(null);
  const [targetRoom, setTargetRoom] = useState<any>(null);
  const [saving, setSaving]         = useState(false);
  const [success, setSuccess]       = useState("");
  const [error, setError]           = useState("");

  // Receipt modal
  const [showReceipt, setShowReceipt] = useState(false);
  const [newRatePerNight, setNewRatePerNight] = useState("");
  const [oldRateOverride, setOldRateOverride] = useState(""); // reception can correct the old rate
  const [rateLoading, setRateLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/api/dashboard/rooms-inhouse`, { headers: authHeaders }),
      axios.get(`${API_BASE}/api/rooms`, { headers: authHeaders }),
      axios.get(`${API_BASE}/api/dashboard/bookings`, { headers: authHeaders }),
    ]).then(([ih, r, b]) => {
      setInHouse((ih.data || []).filter((r: any) => r.occupied));
      setRooms(r.data || []);
      const bkgs = Array.isArray(b.data) ? b.data : b.data?.bookings ?? [];
      setBookings(bkgs);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const today = localISO(new Date());

  const availableForUpgrade = useMemo(() => {
    if (!selected) return [];
    const co = String(selected.booking?.checkOut || "").slice(0, 10);
    return rooms.filter(r => {
      if (r.roomid === selected.roomId) return false;
      if ((r.status || "").toLowerCase() === "out-of-service") return false;
      // Skip rooms occupied by other in-house guests
      if (inHouse.some(ih => ih.roomId === r.roomid && ih.roomId !== selected.roomId)) return false;
      // Check conflicts only for the REMAINING period (today → checkout)
      const hasConflict = bookings.some(b => {
        if (b.roomId !== r.roomid) return false;
        if (["cancelled","deleted","checked-out"].includes((b.status || "").toLowerCase())) return false;
        if (b.bookingId === selected.booking?.bookingId) return false;
        const bci = String(b.checkIn || "").slice(0, 10);
        const bco = String(b.checkOut || "").slice(0, 10);
        return bci < co && bco > today;
      });
      return !hasConflict;
    });
  }, [selected, rooms, bookings, inHouse, today]);

  // When target room is selected, fetch its base rate from the room rates table
  const selectTargetRoom = async (r: any) => {
    setTargetRoom(r);
    setShowReceipt(false);
    setRateLoading(true);
    try {
      // Always fetch base rate from roomrates table — most reliable source
      const ratesRes = await axios.get(`${API_BASE}/api/roomrates`, { headers: authHeaders });
      const rates: any[] = ratesRes.data || [];
      const match = rates.find((rt: any) => String(rt.roomid) === String(r.roomid));
      if (match && match.baserate > 0) {
        setNewRatePerNight(String(match.baserate));
      } else {
        // Try calc endpoint as secondary source
        const checkout = String(selected?.booking?.checkOut || "").slice(0, 10);
        if (checkout && checkout > today) {
          const calcRes = await axios.get(`${API_BASE}/api/roomrates/calc`, {
            params: { roomId: r.roomid, checkin: today, checkout },
            headers: authHeaders,
          });
          const total = calcRes.data?.total ?? 0;
          const nights = calcRes.data?.nights?.length ?? 1;
          if (nights > 0 && total > 0) {
            setNewRatePerNight(String(Math.round(total / nights)));
            return;
          }
        }
        setNewRatePerNight("0");
      }
    } catch {
      setNewRatePerNight("0");
    } finally { setRateLoading(false); }
  };

  // Derived receipt values
  const checkout = String(selected?.booking?.checkOut || "").slice(0, 10);
  const checkin  = String(selected?.booking?.checkIn  || "").slice(0, 10);
  const totalNights = selected?.booking?.nights || 1;
  // Old room price: prefer the per-room price, fall back to the booking total
  // (bot/walk-in bookings often store the price only in `total`, leaving room_price 0).
  const oldRoomPriceRaw = selected?.booking?.roomPrice || selected?.booking?.total || 0;
  const derivedOldRate = Math.round(oldRoomPriceRaw / totalNights);
  // Reception can override the old rate when the booking has no stored price.
  const oldRatePerNight = oldRateOverride !== "" ? (Number(oldRateOverride) || 0) : derivedOldRate;
  const oldRoomPrice = oldRatePerNight * totalNights; // keep totals consistent with the (possibly edited) rate

  const remainingNights = (() => {
    if (!today || !checkout) return 0;
    const t = new Date(today + "T12:00:00");
    const co = new Date(checkout + "T12:00:00");
    return Math.max(0, Math.round((co.getTime() - t.getTime()) / 86400000));
  })();

  const usedNights = Math.max(0, totalNights - remainingNights);
  const newRate = Number(newRatePerNight) || 0;
  const newRoomPriceTotal = usedNights * oldRatePerNight + remainingNights * newRate;
  const extraCharge = newRoomPriceTotal - oldRoomPrice;

  const handleUpgrade = async () => {
    const bookingRoomId = selected?.booking?.bookingRoomId;
    if (!bookingRoomId || !targetRoom) return;
    setSaving(true); setError("");
    try {
      await axios.put(
        `${API_BASE}/api/dashboard/room-swap/${bookingRoomId}`,
        {
          newRoomId: targetRoom.roomid,
          upgradeDate: today,
          newRoomPriceTotal,
          reason: "Guest upgrade",
          swappedBy: "reception",
        },
        { headers: authHeaders }
      );
      setSuccess(`Room ${selected.roomNumber} → Room ${targetRoom.roomnumber} upgrade complete. ${extraCharge > 0 ? `EGP ${extraCharge.toLocaleString()} added to balance.` : ""}`);
      setSelected(null); setTargetRoom(null); setShowReceipt(false); setOldRateOverride("");
      // Refresh in-house list
      const ih = await axios.get(`${API_BASE}/api/dashboard/rooms-inhouse`, { headers: authHeaders });
      setInHouse((ih.data || []).filter((r: any) => r.occupied));
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? "Upgrade failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="fd-loading">Loading…</div>;

  return (
    <div className="fd-upgrade">
      {success && <div className="fd-success"><FontAwesomeIcon icon={faCheck} /> {success}</div>}
      {error   && <div className="fd-error"><FontAwesomeIcon icon={faXmark} /> {error}</div>}

      <div className="fd-upgrade-layout">
        <div className="fd-panel">
          <div className="fd-panel-head">
            <div className="fd-panel-icon fd-panel-icon-navy"><FontAwesomeIcon icon={faUser} /></div>
            <h3 className="fd-panel-title">In-House Guests</h3>
            <span className="fd-panel-count" style={{ background: "rgba(14,34,64,0.08)", color: "#1a3a5c" }}>{inHouse.length}</span>
          </div>
          <div className="fd-room-list">
            {inHouse.map(r => (
              <div
                key={r.roomId}
                className={`fd-room-item ${selected?.roomId === r.roomId ? "selected" : ""}`}
                onClick={() => { setSelected(r); setTargetRoom(null); setShowReceipt(false); setOldRateOverride(""); setSuccess(""); setError(""); }}
              >
                <span className="fd-room-number">Room {r.roomNumber}</span>
                <span className="fd-room-type">{r.booking?.guestName || "—"}</span>
                {selected?.roomId === r.roomId && <FontAwesomeIcon icon={faCheck} className="fd-room-check" />}
              </div>
            ))}
            {inHouse.length === 0 && <div className="fd-empty">No guests currently in-house</div>}
          </div>
        </div>

        {selected && (
          <div className="fd-panel">
            <div className="fd-panel-head">
              <div className="fd-panel-icon fd-panel-icon-gold"><FontAwesomeIcon icon={faArrowUp} /></div>
              <h3 className="fd-panel-title">Upgrade To</h3>
              <span className="fd-panel-count" style={{ background: "rgba(201,168,76,0.1)", color: "#92400e" }}>{availableForUpgrade.length} available</span>
            </div>
            <div className="fd-current-room">
              Currently in: <strong>Room {selected.roomNumber}</strong> · {selected.roomType}
              <span className="fd-current-dates"> · {fmt(checkin)} → {fmt(checkout)} · {remainingNights} night{remainingNights !== 1 ? "s" : ""} remaining</span>
            </div>
            <div className="fd-room-list">
              {availableForUpgrade.map(r => (
                <div
                  key={r.roomid}
                  className={`fd-room-item ${targetRoom?.roomid === r.roomid ? "selected" : ""}`}
                  onClick={() => selectTargetRoom(r)}
                >
                  <span className="fd-room-number">Room {r.roomnumber}</span>
                  <span className="fd-room-type">{r.roomtype || "—"}{r.bed_type ? ` · ${r.bed_type}` : ""}</span>
                  {targetRoom?.roomid === r.roomid && <FontAwesomeIcon icon={faCheck} className="fd-room-check" />}
                </div>
              ))}
              {availableForUpgrade.length === 0 && <div className="fd-empty">No upgrade options available for remaining stay</div>}
            </div>
            {targetRoom && !showReceipt && (
              <div style={{ padding: "0 12px 16px" }}>
                <button className="fd-submit-btn" onClick={() => setShowReceipt(true)} disabled={rateLoading}>
                  <FontAwesomeIcon icon={faArrowUp} />
                  {rateLoading ? "Loading rate…" : `Upgrade to Room ${targetRoom.roomnumber}`}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Upgrade Receipt */}
        {showReceipt && targetRoom && selected && (
          <div className="fd-panel fd-receipt-panel">
            <div className="fd-panel-head">
              <div className="fd-panel-icon fd-panel-icon-gold"><FontAwesomeIcon icon={faMoneyBill} /></div>
              <h3 className="fd-panel-title">Upgrade Receipt</h3>
            </div>
            <div className="fd-receipt">
              <div className="fd-receipt-row fd-receipt-header-row">
                <span>Room {selected.roomNumber}</span>
                <FontAwesomeIcon icon={faArrowRight} style={{ color: "#c9a84c" }} />
                <span>Room {targetRoom.roomnumber}</span>
              </div>
              <div className="fd-receipt-row">
                <span className="fd-receipt-label">Guest</span>
                <span className="fd-receipt-val">{selected.booking?.guestName}</span>
              </div>
              <div className="fd-receipt-row">
                <span className="fd-receipt-label">Stay</span>
                <span className="fd-receipt-val">{fmt(checkin)} → {fmt(checkout)} ({totalNights} nights)</span>
              </div>
              <div className="fd-receipt-row">
                <span className="fd-receipt-label">Nights used</span>
                <span className="fd-receipt-val">{usedNights} night{usedNights !== 1 ? "s" : ""} at EGP {oldRatePerNight.toLocaleString()}/night</span>
              </div>
              <div className="fd-receipt-row">
                <span className="fd-receipt-label">Remaining</span>
                <span className="fd-receipt-val">{remainingNights} night{remainingNights !== 1 ? "s" : ""} from {fmt(today)}</span>
              </div>
              <div className="fd-receipt-divider" />
              <div className="fd-receipt-row fd-receipt-editable">
                <span className="fd-receipt-label">Old rate/night</span>
                <div className="fd-receipt-input-wrap">
                  <span className="fd-receipt-currency">EGP</span>
                  <input
                    className="fd-receipt-input"
                    type="number"
                    min="0"
                    value={oldRateOverride !== "" ? oldRateOverride : String(derivedOldRate)}
                    onChange={e => setOldRateOverride(e.target.value)}
                  />
                </div>
              </div>
              <div className="fd-receipt-row fd-receipt-editable">
                <span className="fd-receipt-label">New rate/night</span>
                <div className="fd-receipt-input-wrap">
                  <span className="fd-receipt-currency">EGP</span>
                  <input
                    className="fd-receipt-input"
                    type="number"
                    min="0"
                    value={newRatePerNight}
                    onChange={e => setNewRatePerNight(e.target.value)}
                  />
                </div>
              </div>
              <div className="fd-receipt-divider" />
              <div className="fd-receipt-row">
                <span className="fd-receipt-label">Old room total</span>
                <span className="fd-receipt-val">EGP {oldRoomPrice.toLocaleString()}</span>
              </div>
              <div className="fd-receipt-row">
                <span className="fd-receipt-label">New room total</span>
                <span className="fd-receipt-val fd-receipt-highlight">EGP {newRoomPriceTotal.toLocaleString()}</span>
              </div>
              {extraCharge !== 0 && (
                <div className="fd-receipt-row">
                  <span className="fd-receipt-label">{extraCharge > 0 ? "Extra charge" : "Discount"}</span>
                  <span className="fd-receipt-val" style={{ color: extraCharge > 0 ? "#ef4444" : "#34d399" }}>
                    {extraCharge > 0 ? "+" : ""}EGP {extraCharge.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            <div className="fd-receipt-actions">
              <button className="fd-btn-ghost" onClick={() => setShowReceipt(false)}>Back</button>
              <button className="fd-submit-btn" onClick={handleUpgrade} disabled={saving}>
                <FontAwesomeIcon icon={faArrowUp} />
                {saving ? "Upgrading…" : "Confirm Upgrade"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function FrontDeskPage() {
  usePricingConfig();
  const [tab, setTab] = useState<"walkin" | "briefing" | "upgrade">("walkin");
  const token      = localStorage.getItem("ai2_token") ?? "";
  const hotel      = localStorage.getItem("ai2_hotel") ?? "";
  const authHeaders = { Authorization: `Bearer ${token}`, "x-hotel": hotel };

  const tabs = [
    { id: "walkin",   icon: faDoorOpen,       label: "Walk-in Booking" },
    { id: "briefing", icon: faClipboardList,  label: "Arrivals Briefing" },
    { id: "upgrade",  icon: faArrowUp,        label: "Room Upgrade" },
  ] as const;

  return (
    <DashboardLayout>
      <div className="fd-page">
        <div className="fd-header">
          <div>
            <h1 className="fd-title"><FontAwesomeIcon icon={faDoorOpen} /> Front Desk</h1>
            <p className="fd-subtitle">Walk-ins · Arrivals · Room Upgrades</p>
          </div>
        </div>

        <div className="fd-tabs no-print">
          {tabs.map(t => (
            <button
              key={t.id}
              className={`fd-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              <FontAwesomeIcon icon={t.icon} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="fd-content">
          {tab === "walkin"   && <WalkInTab   authHeaders={authHeaders} />}
          {tab === "briefing" && <ArrivalsBriefingTab authHeaders={authHeaders} />}
          {tab === "upgrade"  && <UpgradeTab  authHeaders={authHeaders} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
